import classNames from "classnames";
import defaultCloseIcon from "../../assets/arrow-left.svg";
import { NodeStates, SlideoutNode } from "../../build/types";
import { SlideoutProps } from "../BuilderInterface";

import {
    getColorsFromInteractiveStates,
    getCompoundStyle,
    getStateColorsForBuilder,
    getStateStyleForBuilder,
    getStylesFromInteractiveStates
} from "../helpers";
import "./style.scss";
import DynamicIcon from "../DynamicIcon";
import { useRef } from "react";
import { useInteractionStates } from "../../hooks/useInteractionStates";

const BuilderSlideout = ({ node, children, actionCallbacks, builderProps, open, onClose, disabled }: SlideoutProps) => {
    const sNode = node as SlideoutNode;
    const { id, attr, stateManager, slideout } = sNode;
    const { states: wrapperStates, setElement: setWrapperElement } = useInteractionStates<HTMLDivElement>();
    const { states: buttonStates, setElement: setButtonElement } = useInteractionStates<HTMLButtonElement>();

    const div = useRef<HTMLDivElement>(null);

    const slideoutClass = classNames("builder-slideout", {
        [`${attr?.className}`]: !!attr?.className,
        ["left"]: sNode.slideout?.openMode === "pushLeft",
        ["right"]: sNode.slideout?.openMode === "pushRight",
        ["fixed-left"]: sNode.slideout?.openMode === "overlayLeft",
        ["fixed-right"]: sNode.slideout?.openMode === "overlayRight"
    });

    if (builderProps) {
        const { isContainer, nodeState, extraChild, selectCallback, outlineCallback, onDragStart, onDragEnd, onDragOverCapture } =
            builderProps;

        //Seperate state styles
        const slideoutStyles = getCompoundStyle({
            node,
            isContainer,
            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "attr_style")
        });
        const { flexDirection, alignItems, justifyContent, gap, ...slideoutWrapperStyles } = slideoutStyles || {};
        const slideoutBodyStyles = {
            flexDirection,
            alignItems,
            justifyContent,
            gap
        };

        let slideoutHeaderStyle, slideoutHeaderTitleStyle, slidoutHeaderIcon;
        let closeButtonIcon = (
            <img
                src={defaultCloseIcon}
                style={
                    slideout?.openMode === "pushRight" || slideout?.openMode === "overlayRight"
                        ? { transform: "rotate(180deg)" }
                        : {}
                }
            />
        );

        if (slideout?.header?.title?.text || slideout?.header?.closeButton?.visible) {
            slideoutHeaderStyle = getCompoundStyle({
                otherStyle: slideout.header.style,
                stateStyle: getStateStyleForBuilder(stateManager, nodeState, "slideout_header_style")
            });
        }

        if (slideout?.header?.title?.text) {
            slideoutHeaderTitleStyle = getCompoundStyle({
                otherStyle: slideout.header.title.style,
                stateStyle: getStateStyleForBuilder(stateManager, nodeState, "slideout_header_title_style")
            });
        }

        if (slideout?.header?.icon?.src) {
            const slideoutHeaderIconStyle = getCompoundStyle({
                otherStyle: slideout.header.icon.style,
                stateStyle: getStateStyleForBuilder(stateManager, nodeState, "slideout_header_icon_style")
            });

            const slideoutHeaderIconColors =
                getStateColorsForBuilder(stateManager, nodeState, "slideout_header_icon_colors") || slideout.header.icon.colors;

            slidoutHeaderIcon = (
                <DynamicIcon name={slideout.header.icon.src} style={slideoutHeaderIconStyle} colors={slideoutHeaderIconColors} />
            );
        }

        if (slideout?.header?.closeButton?.visible && slideout.header.closeButton.icon?.src) {
            const slideoutHeaderButtonIconStyles = getCompoundStyle({
                otherStyle: slideout.header.closeButton.icon.style,
                stateStyle: getStateStyleForBuilder(stateManager, nodeState, "slideout_header_closeButton_icon_style")
            });

            const slideoutHeaderButtonIconColors =
                getStateColorsForBuilder(stateManager, nodeState, "slideout_header_closeButton_icon_colors") ||
                slideout.header.closeButton.icon.colors;

            closeButtonIcon = (
                <DynamicIcon
                    name={slideout.header.closeButton.icon?.src}
                    style={slideoutHeaderButtonIconStyles}
                    colors={slideoutHeaderButtonIconColors}
                />
            );
        }

        return (
            open && (
                <div
                    className={slideoutClass}
                    onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                    onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                    onDragStart={e => onDragStart?.(e, id)}
                    draggable={"true"}
                    onDragEnd={onDragEnd}
                    style={slideoutWrapperStyles}
                    id={id}
                >
                    {(slideout?.header?.closeButton?.visible || slideout?.header?.title?.text) && (
                        <div className="slideout-header" style={slideoutHeaderStyle}>
                            {slidoutHeaderIcon}
                            {slideout?.header?.title?.text && (
                                <p style={slideoutHeaderTitleStyle}>{slideout.header.title.text}</p>
                            )}
                            {slideout.header.closeButton?.visible && <button>{closeButtonIcon}</button>}
                        </div>
                    )}
                    <div
                        className="slideout-body"
                        ref={div}
                        onDragOverCapture={e => onDragOverCapture?.(e, id, div.current as HTMLDivElement)}
                        style={slideoutBodyStyles}
                    >
                        {children}
                        {extraChild}
                    </div>
                </div>
            )
        );
    } else {
        const slideoutStyles = getStylesFromInteractiveStates({
            states: wrapperStates,
            defaultStyle: getCompoundStyle({ node }),
            stateManager,
            paths: ["attr_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const { flexDirection, alignItems, justifyContent, gap, ...slideoutWrapperStyles } = slideoutStyles;
        const slideoutBodyStyles = { flexDirection, alignItems, justifyContent, gap };

        let slideoutHeaderStyle, slideoutHeaderTitleStyle, slidoutHeaderIcon;

        let closeButtonIcon = (
            <img
                src={defaultCloseIcon}
                style={
                    slideout?.openMode === "pushRight" || slideout?.openMode === "overlayRight"
                        ? { transform: "rotate(180deg)" }
                        : {}
                }
            />
        );

        if (slideout?.header?.title?.text || slideout?.header?.closeButton?.visible) {
            slideoutHeaderStyle = getStylesFromInteractiveStates({
                states: wrapperStates,
                defaultStyle: slideout?.header?.style,
                stateManager,
                paths: ["slideout_header_style"],
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });
        }

        if (slideout?.header?.title?.text) {
            slideoutHeaderTitleStyle = getStylesFromInteractiveStates({
                states: wrapperStates,
                defaultStyle: slideout?.header?.title?.style,
                stateManager,
                paths: ["slideout_header_title_style"],
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });
        }

        if (slideout?.header?.icon?.src) {
            const slideoutHeaderIconStyle = getStylesFromInteractiveStates({
                states: wrapperStates,
                defaultStyle: slideout?.header?.icon?.style,
                stateManager,
                paths: ["slideout_header_icon_style"],
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });

            const slideoutHeaderIconColors = getColorsFromInteractiveStates({
                states: wrapperStates,
                stateManager,
                paths: ["slideout_header_icon_colors"],
                defaultColors: slideout.header.icon.colors,
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });

            slidoutHeaderIcon = (
                <DynamicIcon name={slideout.header.icon.src} style={slideoutHeaderIconStyle} colors={slideoutHeaderIconColors} />
            );
        }

        if (slideout?.header?.closeButton?.visible && slideout.header.closeButton.icon?.src) {
            const slideoutHeaderButtonIconStyles = getStylesFromInteractiveStates({
                states: buttonStates,
                defaultStyle: slideout?.header?.closeButton?.icon?.style,
                stateManager,
                paths: ["slideout_header_closeButton_icon_style"],
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });

            const slideoutHeaderButtonIconColors = getColorsFromInteractiveStates({
                states: buttonStates,
                stateManager,
                paths: ["slideout_header_closeButton_icon_colors"],
                defaultColors: slideout.header.closeButton.icon.colors,
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });

            closeButtonIcon = (
                <DynamicIcon
                    name={slideout.header.closeButton.icon?.src}
                    style={slideoutHeaderButtonIconStyles}
                    colors={slideoutHeaderButtonIconColors}
                />
            );
        }

        return (
            open && (
                <div ref={setWrapperElement} className={slideoutClass} {...actionCallbacks} id={id} style={slideoutWrapperStyles}>
                    {(sNode.slideout?.header?.closeButton?.visible || sNode.slideout?.header?.title?.text) && (
                        <div className="slideout-header" style={slideoutHeaderStyle}>
                            {slidoutHeaderIcon}
                            {sNode.slideout?.header?.title?.text && (
                                <p style={slideoutHeaderTitleStyle}>{sNode.slideout.header.title.text}</p>
                            )}
                            {sNode.slideout.header.closeButton?.visible && (
                                <button ref={setButtonElement} onClick={onClose}>
                                    {closeButtonIcon}
                                </button>
                            )}
                        </div>
                    )}
                    <div className="slideout-body" style={slideoutBodyStyles}>
                        {children}
                    </div>
                </div>
            )
        );
    }
};

export default BuilderSlideout;
